// Rapprochement géométrique du drapeau (docs/routes/README.md §8, lot C, 12 octobre 2026). Module pur, testé.
//
// But (propriétaire) : placer le drapeau sur la ligne entre le trottoir et l'entrée à déneiger ; son approche, dans
// un rayon de 5 m ou moins, fera passer la résidence « en cours ». Méthode :
//   1. point de départ = position d'intervention (ajustée sur l'entrée quand elle existe, sinon le géocodage) ;
//   2. rue desservante = tronçon AQréseau le plus proche dont le nom correspond à l'adresse (sans type de voie,
//      articles, accents ni traits d'union), à moins de `maxSearchMeters` ;
//   3. projection sur l'axe de cette rue, puis drapeau à `setbackMeters` de l'axe, perpendiculairement, du côté de la
//      résidence ;
//   4. abstention motivée quand le résultat serait douteux, avertissement quand il faut vérifier sur place.
// Calculs en mètres dans une projection locale équirectangulaire centrée sur l'adresse (écart négligeable à moins
// d'un kilomètre) : aucune addition arbitraire de degrés.
//
// Limites, à dire à l'utilisateur : l'axe AQréseau n'est pas le trottoir ; la largeur réelle de la rue est inconnue
// (le recul est à calibrer sur le terrain) ; le résultat n'est pas une détection physique de l'entrée.

export interface StreetSegment {
  id: string
  name: string | null
  roadClass: string | null
  /** [[latitude, longitude], …] */
  points: [number, number][]
}

export interface SnapLocation {
  id: string
  addressLine1: string | null
  latitude: number | null
  longitude: number | null
}

export interface SnapSettings {
  /** Distance du drapeau à l'axe de la rue (m) : hypothèse à calibrer sur le terrain. */
  setbackMeters: number
  /** Rue desservante cherchée dans ce rayon (m). */
  maxSearchMeters: number
}

export const defaultSnapSettings: SnapSettings = { setbackMeters: 6, maxSearchMeters: 60 }

/** Rayon de la zone de détection dessinée autour de chaque drapeau (m) : 7 m, demande du propriétaire du 12 oct. 2026. */
export const flagDetectionRadiusMeters = 7

export type SnapAbstainReason = 'UNLOCATED' | 'STREET_NOT_FOUND' | 'ON_AXIS' | 'SETBACK_TOO_LARGE'

export type SnapWarning = 'CORNER'

export interface SnapDetails {
  streetName: string
  roadClass: string | null
  segmentId: string
  /** Distance de la position d'intervention à l'axe (m). */
  axisDistance: number
  setback: number
  /** Distance entre le drapeau proposé et la position d'intervention (m). */
  moveDistance: number
}

export type SnapResult =
  | { locationId: string; point: { lat: number; lng: number }; warnings: SnapWarning[]; details: SnapDetails }
  | {
      locationId: string
      abstainReason: SnapAbstainReason
      warnings: SnapWarning[]
      details: Partial<SnapDetails>
    }

const earthRadius = 6_371_000
const degree = Math.PI / 180

interface Local {
  toXY: (lat: number, lng: number) => { x: number; y: number }
  toLatLng: (x: number, y: number) => { lat: number; lng: number }
}

/** Projection locale (mètres) centrée sur un point. */
export function localFrame(lat0: number, lng0: number): Local {
  const cos = Math.cos(lat0 * degree)
  return {
    toXY: (lat, lng) => ({
      x: (lng - lng0) * degree * earthRadius * cos,
      y: (lat - lat0) * degree * earthRadius,
    }),
    toLatLng: (x, y) => ({
      lat: lat0 + y / (earthRadius * degree),
      lng: lng0 + x / (earthRadius * degree * cos),
    }),
  }
}

/** Types de voie, ramenés à une forme canonique (« Av. », « Ave » → « avenue »). */
const streetTypes: Record<string, string> = {
  rue: 'rue',
  avenue: 'avenue',
  av: 'avenue',
  ave: 'avenue',
  boulevard: 'boulevard',
  boul: 'boulevard',
  blvd: 'boulevard',
  bd: 'boulevard',
  chemin: 'chemin',
  ch: 'chemin',
  montee: 'montee',
  place: 'place',
  pl: 'place',
  impasse: 'impasse',
  rang: 'rang',
  route: 'route',
  rte: 'route',
  croissant: 'croissant',
  cote: 'cote',
  terrasse: 'terrasse',
  carre: 'carre',
  allee: 'allee',
  promenade: 'promenade',
  voie: 'voie',
  cercle: 'cercle',
  cour: 'cour',
  passage: 'passage',
  sentier: 'sentier',
  square: 'square',
}
const particles = new Set(['de', 'du', 'des', 'la', 'le', 'les', 'l', 'd', 'st', 'saint', 'ste', 'sainte'])
/** « 17e », « 17ieme », « 1er », « 2nd » → « 17 », « 1 », « 2 ». */
const ordinal = /^(\d+)(e|eme|ieme|er|re|ere|th|st|nd|rd)$/

function tokens(value: string): string[] {
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((token) => ordinal.exec(token)?.[1] ?? token)
}

export interface StreetName {
  /** Nom propre, sans type de voie, articles, « saint », accents ni ponctuation. */
  core: string
  /** Type de voie canonique, s'il est écrit. */
  type: string | null
}

export function parseStreetName(name: string): StreetName {
  const words = tokens(name)
  const type = words.map((word) => streetTypes[word]).find((value) => value !== undefined) ?? null
  return {
    core: words.filter((word) => streetTypes[word] === undefined && !particles.has(word)).join(' '),
    type,
  }
}

/** Nom de rue comparable (sans type de voie). */
export function streetKey(name: string): string {
  return parseStreetName(name).core
}

/**
 * Même rue ? Noms propres identiques ; pour une rue numérotée (« 22e Rue » et « 22e Avenue » coexistent à
 * Saint-Jérôme), le type de voie doit aussi correspondre quand les deux sont écrits.
 */
export function sameStreet(address: StreetName, segment: StreetName): boolean {
  if (address.core === '' || address.core !== segment.core) return false
  if (address.type === null || segment.type === null) return true
  return address.type === segment.type || !/^\d+$/.test(address.core)
}

/** Rue de la ligne d'adresse : avant la première virgule, sans numéro civique ni unité. */
export function streetOfAddress(addressLine1: string | null): string | null {
  if (!addressLine1) return null
  const head = addressLine1.split(',')[0] ?? ''
  const withoutCivic = head.replace(/^\s*\d+[a-zA-Z]?(\s*[-–]\s*\d+[a-zA-Z]?)?\s+/, '').trim()
  return withoutCivic === '' ? null : withoutCivic
}

interface Projection {
  segment: StreetSegment
  distance: number
  point: { x: number; y: number }
  /** Direction unitaire du morceau de tronçon au point projeté. */
  direction: { x: number; y: number }
  /** Projection en bout de tronçon (pas perpendiculaire). */
  atEnd: boolean
}

/** Point le plus proche d'un tronçon (polyligne), en mètres dans la projection locale. */
function project(segment: StreetSegment, frame: Local): Projection | null {
  let best: Projection | null = null
  const points = segment.points.map(([lat, lng]) => frame.toXY(lat, lng))
  for (let index = 0; index < points.length - 1; index += 1) {
    const a = points[index]
    const b = points[index + 1]
    if (!a || !b) continue
    const vx = b.x - a.x
    const vy = b.y - a.y
    const length2 = vx * vx + vy * vy
    if (length2 === 0) continue
    const raw = -(a.x * vx + a.y * vy) / length2
    const u = Math.max(0, Math.min(1, raw))
    const px = a.x + u * vx
    const py = a.y + u * vy
    const distance = Math.hypot(px, py)
    if (!best || distance < best.distance) {
      const length = Math.sqrt(length2)
      const atFirst = u === 0 && index === 0
      const atLast = u === 1 && index === points.length - 2
      best = {
        segment,
        distance,
        point: { x: px, y: py },
        direction: { x: vx / length, y: vy / length },
        atEnd: (raw < 0 && atFirst) || (raw > 1 && atLast),
      }
    }
  }
  return best
}

/** Seuil en deçà duquel l'adresse est considérée sur l'axe : le côté de la rue est indécidable. */
const onAxisMeters = 1
/** Une autre rue presque aussi proche (écart ≤ 10 m, à moins de 30 m) signale un coin de rue à vérifier. */
const cornerSlackMeters = 10
const cornerMaxMeters = 30

export function snapFlag(
  location: SnapLocation,
  segments: readonly StreetSegment[],
  settings: SnapSettings,
): SnapResult {
  const { id: locationId } = location
  if (location.latitude === null || location.longitude === null) {
    return { locationId, abstainReason: 'UNLOCATED', warnings: [], details: {} }
  }
  const frame = localFrame(location.latitude, location.longitude)
  const street = streetOfAddress(location.addressLine1)
  const wanted = street ? parseStreetName(street) : { core: '', type: null }
  const projections = segments
    .map((segment) => project(segment, frame))
    .filter(
      (projection): projection is Projection =>
        projection !== null && projection.distance <= settings.maxSearchMeters,
    )
    .sort((left, right) => left.distance - right.distance)
  const matching = projections.filter(
    (item) => item.segment.name && sameStreet(wanted, parseStreetName(item.segment.name)),
  )
  // Même type de voie d'abord (« Rue des Pins » plutôt qu'« Avenue des Pins » voisine), sinon le plus proche.
  const serving =
    matching.find((item) => item.segment.name && parseStreetName(item.segment.name).type === wanted.type) ??
    matching[0]
  if (!serving) return { locationId, abstainReason: 'STREET_NOT_FOUND', warnings: [], details: {} }

  const base = {
    streetName: serving.segment.name ?? '',
    roadClass: serving.segment.roadClass,
    segmentId: serving.segment.id,
    axisDistance: round(serving.distance),
    setback: settings.setbackMeters,
  }
  const warnings: SnapWarning[] = []
  const servingCore = serving.segment.name ? parseStreetName(serving.segment.name).core : ''
  const other = projections.find(
    (item) =>
      item.segment.name &&
      parseStreetName(item.segment.name).core !== servingCore &&
      item.distance <= cornerMaxMeters,
  )
  if (other && other.distance <= serving.distance + cornerSlackMeters) warnings.push('CORNER')

  if (serving.distance < onAxisMeters)
    return { locationId, abstainReason: 'ON_AXIS', warnings, details: base }
  if (settings.setbackMeters >= serving.distance) {
    return { locationId, abstainReason: 'SETBACK_TOO_LARGE', warnings, details: base }
  }
  // Côté de la résidence : perpendiculaire à l'axe, orientée vers l'adresse (origine de la projection locale).
  const toward = { x: -serving.point.x / serving.distance, y: -serving.point.y / serving.distance }
  let normal = { x: -serving.direction.y, y: serving.direction.x }
  if (normal.x * toward.x + normal.y * toward.y < 0) normal = { x: -normal.x, y: -normal.y }
  // En bout de tronçon, la projection n'est pas perpendiculaire : on suit la direction vers l'adresse.
  const offset = serving.atEnd ? toward : normal
  const x = serving.point.x + offset.x * settings.setbackMeters
  const y = serving.point.y + offset.y * settings.setbackMeters
  const point = frame.toLatLng(x, y)
  return {
    locationId,
    point: { lat: round6(point.lat), lng: round6(point.lng) },
    warnings,
    details: { ...base, moveDistance: round(Math.hypot(x, y)) },
  }
}

function round(value: number): number {
  return Math.round(value * 10) / 10
}

function round6(value: number): number {
  return Math.round(value * 1e6) / 1e6
}

/** Distance (m) entre deux points géographiques. */
export function metersBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const frame = localFrame(a.lat, a.lng)
  const p = frame.toXY(b.lat, b.lng)
  return Math.hypot(p.x, p.y)
}

export const snapAbstainTexts: Record<SnapAbstainReason, string> = {
  UNLOCATED: 'Adresse à localiser',
  STREET_NOT_FOUND: 'Rue de l’adresse introuvable à proximité',
  ON_AXIS: 'Position sur l’axe de la rue : côté indécidable',
  SETBACK_TOO_LARGE: 'Position plus proche de l’axe que le recul demandé',
}

export const snapWarningTexts: Record<SnapWarning, string> = {
  CORNER: 'Coin de rue : vérifier la rue desservante',
}
